
import { Point } from './point';
import { Tile, TileType, isValueTile } from './tile';
import { 
  TILE_CLICKED,
  MAP_DIFFICULTY_CHANGED,
  MAP_CODE_ENTERED,
  ADD_TERRAIN,
  UNHOVER_TILE,
  HOVER_TILE,
  SET_EDITOR_TYPE,
  MAP_NAME_CHANGED,
  ENABLE_TERRAIN_EDITOR,
  DISABLE_TERRAIN_EDITOR,
  SET_ACTIVE_TERRAIN
} from './actions';

export interface IAppState {
  map: Array<Array<Tile>>;
  hoverTile: Point;
  editorType: TileType,
  mapName: string,
  mapDifficulty: string,
  terrainEditorEnabled: boolean,
  terrains: Array<string>,
  activeTerrain: string,
  neighbours: Array<any>,
  terrainStatistics: object
}

const mapSize = function(map) {
  return {x: map[0].length, y: map.length};
}

const getTerrainStatistics = function(map) {
  let terrains = {};
  let startTerrain = null;
  let endTerrain = null;
  let unassigned = 0;
  let startHull = [];
  let winnable = false;
  let blockades = {};
  
  // get all terrains
  map.forEach((row, y) => {
    row.forEach((tile, x) => {
      if (!tile || !tile.type) return;
      
      if (tile.terrain) {
        if (!(tile.terrain in terrains)) {
          terrains[tile.terrain] = {
            name: tile.terrain,
            connectedTerrains: [],
            count: 0
          };
        }

        terrains[tile.terrain].count++;

        const neighbours = getNeighbours({x: x, y: y}, map).map(point => map[point.y][point.x]).filter(tile => tile);
        const fromDifferentTerrain = neighbours.filter(nTile => nTile.terrain && nTile.terrain != tile.terrain);

        fromDifferentTerrain.forEach(nTile => {
          if (terrains[tile.terrain].connectedTerrains.indexOf(nTile.terrain) === -1) {
            terrains[tile.terrain].connectedTerrains.push(nTile.terrain);
          }
        });

        if (tile.type === 's') {
          startTerrain = tile.terrain;
        }
        if (tile.type === 'ew' || tile.type === 'ef') {
          endTerrain = tile.terrain;
        }
      }
      else {
        unassigned++;
      }
    });
  });

  const getConvexHull = function(terrain, accumulator) {
    if (!(terrain in terrains)) return accumulator;

    accumulator.push(terrain);

    for (let i = 0; i < terrains[terrain].connectedTerrains.length; i++) {
      let cT = terrains[terrain].connectedTerrains[i];

      if (accumulator.indexOf(cT) === -1) {
        accumulator = getConvexHull(terrains[terrain].connectedTerrains[i], accumulator);
      }
    }

    return accumulator;
  }

  if (startTerrain) {
    startHull = getConvexHull(startTerrain, []);

    if (endTerrain) {
      winnable = startHull.indexOf(endTerrain) !== -1;
    }

    // find blockades
    let validTerrains = Object.keys(terrains)
      .map(k => terrains[k])
      .filter(t => !endTerrain || (endTerrain && endTerrain !== t.name))
      .map(t => Object.assign({}, t, {
        distanceFromStart: startHull.indexOf(t.name),
        connectedTerrains: t.connectedTerrains.sort((a, b) => startHull.indexOf(b) - startHull.indexOf(a))
      }))
      .filter(t => t.distanceFromStart !== -1)
      .sort((a, b) => b.distanceFromStart - a.distanceFromStart);

    for (let i = 0; i < validTerrains.length; i++) {
      let bT = validTerrains[i];
      
      bT.connectedTerrains.forEach(t => {
        let blockadeId = [bT.name, t].sort().join('/');

        if (!(blockadeId in blockades)) {
          blockades[blockadeId] = [bT, terrains[t]];
        }
      });
    }
  }

  return {
    blockades: blockades,
    blockadesSummary: Object.keys(blockades).join(', '),
    terrains: terrains,
    startTerrain: startTerrain,
    endTerrain: endTerrain,
    unassigned: unassigned,
    startHull: startHull,
    winnable: winnable
  };
}

const extendMap = function(map) {
  let m = map.slice(0);

  // top/bottom
  if (!m[0].every(v => !v || !v.type)) m.unshift(new Array<string>(mapSize(m).x), new Array<string>(mapSize(m).x));
  if (!m[mapSize(m).y - 1].every(v => !v || !v.type)) m.push(new Array<string>(mapSize(m).x));

  // left/right
  let left = [];
  let right = [];
  m.forEach(row => {
    left.push(row[0]);
    right.push(row[mapSize(m).x - 1]);
  });

  if (!left.every(v => !v || !v.type)) m.forEach(row => row.unshift(null));
  if (!right.every(v => !v || !v.type))  m.forEach(row => row.push(null));

  return m;
}

const getNeighbours = function(point: Point, map: Array<any>):Array<Point> {
  let neighbours = [];
  let size = mapSize(map);

  if (point.y < size.y - 1) neighbours.push({x: point.x + 0, y: point.y + 1});
  if (point.x < size.x - 1) neighbours.push({x: point.x + 1, y: point.y + 0});
  if (point.x > 0)          neighbours.push({x: point.x - 1, y: point.y + 0});
  if (point.y > 0)          neighbours.push({x: point.x + 0, y: point.y - 1});
  if (point.x < size.x - 1 &&
      point.y > 0)          neighbours.push({x: point.x + 1, y: point.y - 1});
  if (point.x > 0 &&
      point.y < size.y - 1) neighbours.push({x: point.x - 1, y: point.y + 1});

  return neighbours;
}

const getTerrains = function(tiles) {
  let terrains = {};
  tiles.forEach(row => row.forEach(tile => {
    if (tile && tile.terrain) {
      terrains[tile.terrain] = true;
    }
  }));

  return Object.keys(terrains);
}

const initialMap = [
  [null, null, null],
  [null, {type: 's', value: null}, null],
  [null, null, null],
];

export const INITIAL_STATE: IAppState = {
  map: initialMap,
  terrainStatistics: getTerrainStatistics(initialMap),
  hoverTile: null,
  editorType: TileType.FORREST,
  mapName: '',
  mapDifficulty: 'easy',
  terrainEditorEnabled: false,
  terrains: [],
  activeTerrain: '',
  neighbours: []
}

export function rootReducer(state, action) {
  switch (action.type) {
    case MAP_CODE_ENTERED:
      return Object.assign({}, state, {
        mapName: action.payload.code.name,
        map: action.payload.code.tiles,
        mapDifficulty: action.payload.code.difficulty,
        terrains: getTerrains(action.payload.code.tiles)
      });
    case MAP_NAME_CHANGED:
      let name = action.payload.name;
      return Object.assign({}, state, {
        mapName: name
      });
    case MAP_DIFFICULTY_CHANGED:
      let difficulty = action.payload.difficulty.toLowerCase();
      return Object.assign({}, state, {
        mapDifficulty: difficulty
      });
    case ENABLE_TERRAIN_EDITOR:
      return Object.assign({}, state, {
        terrainEditorEnabled: true
      });
    case DISABLE_TERRAIN_EDITOR:
      return Object.assign({}, state, {
        terrainEditorEnabled: false
      });
    case SET_ACTIVE_TERRAIN:
      return Object.assign({}, state, {
        activeTerrain: action.payload.terrain
      });
    case ADD_TERRAIN:
      let terrains = state.terrains;
      terrains.push(action.payload.name);
      return Object.assign({}, state, {
        terrains: terrains,
        terrainStatistics: getTerrainStatistics(state.map)
      });
    case HOVER_TILE:
      let neighbours = getNeighbours(action.payload.point, state.map);

      return Object.assign({}, state, {
        hoverTile: Object.assign({}, state.map[action.payload.point.y][action.payload.point.x], action.payload.point),
        highlightedTiles: neighbours,
        neighbours: neighbours.map(n => state.map[n.y][n.x]).filter(t => t)
      });
    case UNHOVER_TILE:
      return Object.assign({}, state, {
        hoverTile: null,
        highlightedTiles: [],
        neighbours: []
      });
    case SET_EDITOR_TYPE:
      return Object.assign({}, state, {
        editorType: action.payload.type
      });
    case TILE_CLICKED:
      let p = action.payload.point;
      let tile = state.map[p.y][p.x] || {};

      let currentType = tile.type;
      let currentValue = tile.value;
      let currentTerrain = tile.terrain;

      action.map = state.map.slice(0);

      if (state.terrainEditorEnabled) {
        if (state.activeTerrain) {
          tile.terrain = state.activeTerrain;
        }
        else {
          delete tile.terrain;
        }

        action.map[p.y][p.x] = tile;

        return Object.assign({}, state, {
          map: action.map,
          terrainStatistics: getTerrainStatistics(state.map)
        });
      }


      let newType = state.editorType;
      let newValue = currentType !== newType ? 1 : (currentValue ? currentValue + 1 : 1);

      if (isValueTile(newType)) {
        if (newValue > 4) {
          newValue = null;
          newType = null;
        }
      }
      else {
        newValue = null;

        if (currentType) {
          newType = null;
        }
      }

      if (p.y < 0 || p.y >= state.map.length ||
          p.x < 0 || p.x >= state.map[p.y].length) {
        console.log("CANNOT SET TILE", p);
        return state;
      }

      action.map[p.y][p.x] = {
        type: newType,
        value: newValue,
        terrain: currentTerrain
      };

      // extend map if point is on a boundary
      action.map = extendMap(action.map);

      return Object.assign({}, state, {
        map: action.map,
        terrainStatistics: getTerrainStatistics(state.map)
      });
  }

  return state;
}


// WEBPACK FOOTER //
// ./src/app/store.ts